import axios, { AxiosInstance, AxiosError } from 'axios';
import type { APIError } from '@shared/types';
import { getToken, clearSession } from './storage';

/**
 * Use VITE_API_URL for production deployments (Netlify + Render)
 * Falls back to /api for local development with Vite proxy
 */
const getApiUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl && apiUrl.startsWith('http')) {
    return apiUrl;
  }
  return '/api'; // Local dev fallback
};

/**
 * Build full API URL for components that use fetch() or EventSource()
 * instead of axios. Used for direct fetch calls and SSE connections.
 * @param path - Relative path without leading slash (e.g., "shops/123/status")
 * @returns Full URL (e.g., "https://api.example.com/api/shops/123/status" or "/api/shops/123/status")
 */
export const buildApiUrl = (path: string): string => {
  const baseUrl = getApiUrl();
  // baseUrl already includes /api, so just append the path
  return `${baseUrl}/${path}`;
};

/**
 * Axios instance with common config
 */
const api: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor: Attach JWT token to all requests
 */
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: Handle common errors + token expiry
 * 
 * On 401 (Unauthorized):
 * 1. Clears stored auth data
 * 2. Emits 'auth:token-expired' event for UI to listen to
 * 3. UI can show notification, then redirect via router
 * (Avoids hard window.location redirect which breaks SPA state)
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<APIError>) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      clearSession();
      
      // Emit event for UI to handle gracefully (show notification, redirect via router)
      window.dispatchEvent(new CustomEvent('auth:token-expired', {
        detail: {
          message: 'Your session has expired. Please log in again.',
          timestamp: Date.now(),
        },
      }));
    }

    // Extract error message
    const errorData = error.response?.data?.error;
    const message =
      errorData?.message || error.message || 'An unexpected error occurred';

    console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}:`, {
      status: error.response?.status,
      code: errorData?.code,
      message,
      details: errorData?.details,
    });

    return Promise.reject(error);
  }
);

export default api;

/**
 * Helper function to extract user-friendly error message
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as APIError | undefined;
    return apiError?.error?.message || error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Helper to check if error is specific type
 */
export const isAPIError = (error: unknown, code?: string): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (!code) return true;
  return (error.response?.data as APIError | undefined)?.error?.code === code;
};
